import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Printer, Save, RotateCcw, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ReceiptForm } from "@/components/ReceiptForm";
import { FitWidth } from "@/components/FitWidth";
import { partOf } from "@/lib/spot";
import { ReceiptPaper } from "@/components/ReceiptPaper";
import { ShareActions } from "@/components/ShareActions";
import { ReceiptStatusBar } from "@/components/ReceiptStatusBar";
import { ReturningBanner } from "@/components/ReturningBanner";
import { MissingPayments } from "@/components/MissingPayments";
import { computeReceipt, blankComputed, inr } from "@/lib/calc";
import { api, blankInputs } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useFeeMaster, useSettings } from "@/lib/hooks";

const Stat = ({ label, value, testid, accent, gold }) => (
  <div className={`rounded-lg border px-4 py-3 ${gold ? "border-gold-300 border-t-[3px] border-t-gold-500 bg-gold-50" : "border-plum-900/10 bg-white"}`}>
    <div className={`text-[11px] uppercase tracking-wider ${gold ? "text-gold-800" : "text-slate-500"}`}>{label}</div>
    <div className={`font-num mt-1 text-lg font-semibold ${accent ? "text-plum-700" : "text-plum-950"}`} data-testid={testid}>{value === "" ? "—" : `₹ ${inr(value)}`}</div>
  </div>
);

export default function Builder() {
  const [params] = useSearchParams();
  const editId = params.get("edit");
  const nav = useNavigate();
  const qc = useQueryClient();
  const { data: fm } = useFeeMaster();
  const { data: settings } = useSettings();
  const { user } = useAuth();
  const me = user.name || user.email;
  const [v, setV] = useState(blankInputs(me, user.mobile));
  const [receiptNo, setReceiptNo] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!editId) { setReceiptNo(null); setV(blankInputs(me, user.mobile)); return; }
    api.get(`/receipts/${editId}`).then((r) => { const b = blankInputs(me, user.mobile); setV({ ...b, ...r.data.inputs, sub: { ...b.sub, ...(r.data.inputs.sub || {}) } }); setReceiptNo(r.data.receipt_no); })
      .catch(() => toast.error("Receipt not found"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editId]);
  useEffect(() => { if (!editId) setV((x) => ({ ...x, counselorName: me, counselorMobile: user.mobile || "" })); }, [editId, me, user.mobile]);

  const { data: saved } = useQuery({ queryKey: ["receipt", editId], queryFn: () => api.get(`/receipts/${editId}`).then((x) => x.data), enabled: !!editId });
  const base = process.env.REACT_APP_BACKEND_URL;
  const sigUrl = editId
    ? `${base}/api/receipts/${editId}/signature?v=${user.signature_updated_at || ""}`
    : user.has_signature ? `${base}/api/users/${user.id}/signature?v=${user.signature_updated_at}` : null;
  const c = useMemo(() => {
    if (!fm) return null;
    return v.grade ? computeReceipt(fm.grades, v) : blankComputed(computeReceipt(fm.grades, { ...v, grade: fm.order[0] }));
  }, [fm, v]);

  const [prevVisits, setPrevVisits] = useState([]);
  useEffect(() => {
    const q = { contact: v.contact || "", email: v.email || "", childName: v.childName || "", parentName: v.parentName || "", exclude: editId || "" };
    if (!q.contact.replace(/\D/g, "").length && !q.email && !q.childName.trim() && !q.parentName.trim()) { setPrevVisits([]); return; }
    let live = true;
    const t = setTimeout(() => api.post("/receipts/lookup", q).then((r) => live && setPrevVisits(r.data.matches)).catch(() => {}), 500);
    return () => { live = false; clearTimeout(t); };
  }, [v.contact, v.email, v.childName, v.parentName, editId]);
  const today = v.enquiryDate || new Date().toLocaleDateString("en-CA");
  const strongEarlier = prevVisits.some((m) => m.strong && m.visit_date < today);
  useEffect(() => {
    if (strongEarlier && !editId) {
      setV((x) => (x.spot ? { ...x, spot: false, revisit: true } : x));
    }
  }, [strongEarlier, editId]);
  const visible = prevVisits;

  const save = async () => {
    if (!v.childName.trim()) return toast.error("Child name is required");
    if (!v.grade) return toast.error("Select the admission seeking grade");
    if (v.sub.referral && !(v.referralName || "").trim()) return toast.error("Referral Name is required");
    if (v.sub.referral && (v.referralMobile || "").replace(/\D/g, "").length < 10) return toast.error("Enter a valid Referral Mobile No. (at least 10 digits)");
    if (v.sub.existingSibling && !(v.siblingName || "").trim()) return toast.error("Sibling Full Name is required");
    if (v.sub.existingSibling && !v.siblingGrade) return toast.error("Select the sibling's grade");
    setSaving(true);
    try {
      const body = { inputs: v, computed: c };
      const r = editId ? await api.put(`/receipts/${editId}`, body) : await api.post("/receipts", body);
      setReceiptNo(r.data.receipt_no);
      qc.invalidateQueries({ queryKey: ["receipts"] });
      qc.invalidateQueries({ queryKey: ["receipt", r.data.id] });
      toast.success(`Receipt ${r.data.receipt_no} ${editId ? "updated" : "saved"}`);
      if (!editId) nav(`/?edit=${r.data.id}`, { replace: true });
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save receipt"); }
    setSaving(false);
  };

  if (!c) return <div className="flex items-center gap-2 text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading fee master…</div>;

  return (
    <div>
      <div className="no-print mb-4 flex flex-wrap items-end justify-between gap-4">
        <div className="fade-up">
          <div className="text-xs uppercase tracking-[0.2em] text-plum-700">{editId ? `Editing ${receiptNo || ""}` : "New receipt"}</div>
          <h1 className="font-display mt-0.5 text-3xl font-bold leading-tight tracking-tight text-plum-950 sm:text-4xl">Receipt Builder</h1>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => { setV(blankInputs(me, user.mobile)); setReceiptNo(null); nav("/"); }} data-testid="new-receipt-btn"><RotateCcw className="mr-2 h-4 w-4" />New</Button>
          <ShareActions inputs={v} c={c} receiptNo={receiptNo} receiptId={editId} schoolName={settings?.school_name} />
          <Button variant="outline" onClick={() => window.print()} data-testid="print-receipt-btn"><Printer className="mr-2 h-4 w-4" />Print / PDF</Button>
          <Button onClick={save} disabled={saving} data-testid="save-receipt-btn" className="bg-plum-700 hover:bg-plum-800">
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}{editId ? "Update" : "Save"} Receipt
          </Button>
        </div>
      </div>

      {editId && <ReceiptStatusBar id={editId} />}
      {!editId && <MissingPayments />}
      <ReturningBanner matches={visible} strongEarlier={strongEarlier} isRevisit={v.revisit}
        query={{ contact: v.contact || "", email: v.email || "", childName: v.childName || "", parentName: v.parentName || "" }}
        onRevisit={() => setV((x) => ({ ...x, spot: false, revisit: true }))} />

      <div className="no-print mb-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Stat label="At Admission" value={c.atAdmission} testid="stat-at-admission" gold />
        <Stat label="School Fee (after disc.)" value={c.schoolFeeAfterDisc} testid="stat-school-fee" gold />
        <Stat label="Total Discount" value={c.totalDiscount} testid="stat-total-discount" accent />
        <Stat label={v.grade ? `Monthly × ${c.months}` : "Monthly"} value={c.monthly} testid="stat-monthly" />
      </div>

      <div className="grid gap-6 lg:grid-cols-12">
        <div className="no-print min-w-0 lg:col-span-5">
          <ReceiptForm v={v} set={setV} grades={fm.order} spotLocked={strongEarlier} />
        </div>
        <div className="min-w-0 lg:col-span-7">
          <div className="grid-bg rounded-lg border border-plum-900/10 p-3 sm:p-6 lg:sticky lg:top-24">
            <FitWidth>
              <ReceiptPaper inputs={v} c={c} settings={settings} receiptNo={receiptNo} payment={saved?.status === "admitted" ? saved.payment : null} formFee={editId ? saved?.form_fee : null} part={editId ? partOf(saved) : null} signatureUrl={sigUrl} />
            </FitWidth>
          </div>
        </div>
      </div>
    </div>
  );
}
